import {
  Box,
  Card,
  Divider,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  addYears,
  differenceInMonths,
  differenceInYears,
  format,
  parse,
  startOfDay,
} from "date-fns";
import type { THomeInfo } from "../../api/member/detail";
import { LoginUserContext } from "../../providers/LoginUserProvider";
import { getSubdomain } from "../../utils/getSubdomain";
import { routes } from "../../router/Router";
import BottomNavigationBar from "../organisms/BottomNavigationBar";
import BaseHead from "../templates/BaseHead";
import "../../resources/CKEEditor.css";

const useStyles = makeStyles((theme) => ({
  card: {
    width: "100%",
    maxWidth: 992,
    border: "1px solid #C7C7C7",
    borderRadius: "24px",
    background: "transparent",
    paddingBottom: 12,
    backgroundColor: "rgba(256, 256, 256, 0.9)",
    boxShadow: "none",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: "#000",
    fontSize: 20,
    fontWeight: 700,
    padding: "16px 8px",
    [theme.breakpoints.up("md")]: {
      fontSize: 24,
      padding: "24px 40px",
    },
  },
  row: {
    display: "flex",
    flexDirection: "column",
    padding: "16px 24px",
    gridGap: "4px",
    [theme.breakpoints.up("md")]: {
      flexDirection: "row",
      padding: "24px 40px",
      gridGap: "24px",
    },
  },
  label: {
    fontSize: "14px",
    color: "#6B6B6B",
    fontWeight: 700,
    minWidth: 160,
    [theme.breakpoints.up("md")]: {
      fontSize: "16px",
    },
  },
  labelLineBreakPc: {
    [theme.breakpoints.up("md")]: {
      "& > span": {
        display: "block",
      },
    },
  },
  value: {
    fontSize: "16px",
    color: "#0E0E0E",
    wordBreak: "break-word",
    [theme.breakpoints.up("md")]: {
      fontSize: "18px",
    },
  },
  divider: {
    backgroundColor: "#D3D3D3",
  },
}));

const formatAddress = (homeInfo: THomeInfo): string => {
  const zip = homeInfo.zipcode ? `〒${homeInfo.zipcode} ` : "";
  const pref = homeInfo.pref_name ?? "";
  const city = homeInfo.city_name ?? "";
  const address = homeInfo.address ?? "";
  const body = `${pref}${city}${address}`;
  return `${zip}${body}`.trim();
};

const formatHandoverDate = (handoverDate: string): string => {
  const d = parse(handoverDate, "yyyy-MM-dd", new Date());
  if (Number.isNaN(d.getTime())) return handoverDate;
  return format(d, "yyyy年M月d日");
};

const calculateElapsedTime = (handoverDate: string): string => {
  const handover = parse(handoverDate, "yyyy-MM-dd", new Date());
  if (Number.isNaN(handover.getTime())) return "";
  const today = startOfDay(new Date());
  if (today < handover) return "";
  const years = differenceInYears(today, handover);
  const months = differenceInMonths(today, addYears(handover, years));
  return `${years}年${months}ヶ月`;
};

export const MyHomeInfo: React.FC = () => {
  const classes = useStyles();
  const subdomain = getSubdomain(window.location.hostname);
  const history = useHistory();
  const { member } = useContext(LoginUserContext);
  const homeInfo: THomeInfo | null = member?.homeInfo ?? null;
  const canShowHomeInfo = member !== undefined && member.isPtBuilders;

  useEffect(() => {
    if (member && !member.isPtBuilders) {
      history.push(routes.error.path);
    }
  }, [member, history]);

  const [isPwa, setIsPwa] = useState(false);
  useEffect(() => {
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone ===
        true;
    setIsPwa(standalone);
  }, []);

  const additionalInfoRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!isPwa) return;
    const root = additionalInfoRef.current;
    if (!root) return;
    root.querySelectorAll("a[href]").forEach((a) => {
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
    });
  }, [isPwa, homeInfo?.home_additional_info]);

  const addressText = homeInfo ? formatAddress(homeInfo) : "";
  const elapsedText = homeInfo?.handover_date
    ? calculateElapsedTime(homeInfo.handover_date)
    : "";

  return (
    <BaseHead
      title={routes.myHomeInfo.title}
      login
      subdomain={subdomain}
      hasNavigationBar
    >
      <Box display="flex" justifyContent="center" mt={4} mx={2}>
        <Card
          className={classes.card}
          style={{ minHeight: "calc(100vh - 384px)" }}
        >
          <Typography className={classes.cardHeader}>
            住まいの情報
          </Typography>
          <Divider className={classes.divider} />
          {!canShowHomeInfo ? null : (
            <>
              <Box className={classes.row}>
                <Typography className={classes.label}>住所</Typography>
                <Typography className={classes.value}>
                  {addressText || "-"}
                </Typography>
              </Box>
              <Divider className={classes.divider} />
              <Box className={classes.row}>
                <Typography className={classes.label}>引渡日</Typography>
                <Typography className={classes.value}>
                  {homeInfo?.handover_date
                    ? formatHandoverDate(homeInfo.handover_date)
                    : "-"}
                </Typography>
              </Box>
              <Divider className={classes.divider} />
              <Box className={classes.row}>
                <Typography
                  className={`${classes.label} ${classes.labelLineBreakPc}`}
                >
                  <span>お引き渡しからの</span>
                  <span>経過期間</span>
                </Typography>
                <Typography className={classes.value}>
                  {elapsedText || "-"}
                </Typography>
              </Box>
              <Divider className={classes.divider} />
              {(homeInfo?.custom_fields ?? []).map((field, idx) => (
                <Fragment key={`${field.label}-${idx}`}>
                  <Box className={classes.row}>
                    <Typography className={classes.label}>
                      {field.label}
                    </Typography>
                    <Typography className={classes.value}>
                      {field.value ?? "-"}
                    </Typography>
                  </Box>
                  <Divider className={classes.divider} />
                </Fragment>
              ))}
              <Box className={classes.row}>
                <Typography className={classes.label}>その他</Typography>
                <Box className={classes.value}>
                  {homeInfo?.home_additional_info ? (
                    <div
                      ref={additionalInfoRef}
                      className="ck-content"
                      dangerouslySetInnerHTML={{
                        __html: homeInfo.home_additional_info,
                      }}
                    />
                  ) : (
                    "-"
                  )}
                </Box>
              </Box>
            </>
          )}
        </Card>
      </Box>
      <div>
        <BottomNavigationBar />
      </div>
    </BaseHead>
  );
};
